import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, DollarSign, Globe, Phone, Download, Tag } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import MapView from "@/components/MapView";

export default function PlaceDetail() {
  const { slug } = useParams();
  const { t, tx } = useI18n();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/places/${slug}`)
      .then(r => setPlace(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-5xl mx-auto px-5 py-20 text-muted-foreground">{t.common.loading}</div>;
  if (error || !place) return (
    <div className="max-w-5xl mx-auto px-5 py-20">
      <p className="text-muted-foreground">{error || t.common.noResults}</p>
      <Link to="/places" className="mt-4 inline-flex items-center gap-2 text-[hsl(var(--primary-green))]"><ArrowLeft className="w-4 h-4" /> {t.common.back}</Link>
    </div>
  );

  const cat = place.category;
  const mapData = place.latitude && place.longitude ? {
    center: { lat: place.latitude, lng: place.longitude },
    zoom: 15,
    markers: [{
      id: place.id, slug: place.slug,
      title: place.title, short_description: place.short_description,
      latitude: place.latitude, longitude: place.longitude,
      image_url: place.image_url, category: cat,
    }],
  } : null;

  return (
    <div>
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
        {place.image_url ? (
          <img src={place.image_url} alt={tx(place.title)} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-muted" />
        )}
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative h-full max-w-7xl mx-auto px-5 lg:px-8 flex flex-col justify-end pb-10">
          <Link to="/places" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-5 w-fit">
            <ArrowLeft className="w-4 h-4" /> {t.common.back}
          </Link>
          {cat && (
            <span className={`inline-flex w-fit items-center px-3 py-1 rounded-full text-white text-xs font-semibold ${cat.color === "red" ? "bg-[hsl(var(--primary-red))]" : "bg-[hsl(var(--primary-green))]"}`}>
              {tx(cat.name)}
            </span>
          )}
          <h1 className="mt-4 font-display text-4xl md:text-6xl font-semibold tracking-tight text-white" data-testid="place-detail-title">{tx(place.title)}</h1>
          {tx(place.short_description) && (
            <p className="mt-3 text-white/90 text-lg max-w-3xl">{tx(place.short_description)}</p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {tx(place.description) && (
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-3">About</h2>
              <p className="text-foreground/90 leading-relaxed whitespace-pre-line">{tx(place.description)}</p>
            </section>
          )}

          {place.tags && place.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {place.tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm">
                  <Tag className="w-3 h-3" /> {tag}
                </span>
              ))}
            </div>
          )}

          {mapData && (
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-3">{t.sections.mapTitle}</h2>
              <MapView data={mapData} height="380px" />
            </section>
          )}

          {place.downloads && place.downloads.length > 0 && (
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-3">{t.sections.downloads}</h2>
              <div className="space-y-2">
                {place.downloads.map(d => (
                  <a
                    key={d.id}
                    href={`${api.defaults.baseURL}/files/${d.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`place-download-${d.id}`}
                    className="flex items-center gap-3 p-4 bg-card border border-border rounded-2xl hover:border-[hsl(var(--primary-green))] transition"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-green))]/10 text-[hsl(var(--primary-green))] flex items-center justify-center">
                      <Download className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{d.title}</p>
                      <p className="text-xs text-muted-foreground">{d.extension.toUpperCase()} · {Math.round(d.size / 1024)} KB</p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-3">
          <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
            {place.address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[hsl(var(--primary-red))] mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">{t.common.address}</p>
                  <p className="text-sm">{place.address}</p>
                </div>
              </div>
            )}
            {place.schedule && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[hsl(var(--primary-green))] mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">{t.common.schedule}</p>
                  <p className="text-sm">{place.schedule}</p>
                </div>
              </div>
            )}
            {place.price_range && (
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-[hsl(var(--primary-green))] mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">{t.common.price}</p>
                  <p className="text-sm">{place.price_range}</p>
                </div>
              </div>
            )}
            {place.website && (
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-foreground mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">{t.common.website}</p>
                  <a href={place.website} target="_blank" rel="noreferrer" className="text-sm text-[hsl(var(--primary-green))] underline break-all">{place.website}</a>
                </div>
              </div>
            )}
            {place.phone && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-foreground mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">{t.common.phone}</p>
                  <p className="text-sm">{place.phone}</p>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
